import React, { useRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getAnswerIndices } from 'Util/ExamRoom/examHelper';
import useCompatibleDisplay from 'Util/Hooks/useCompatibleDisplay';
import { ButtonFilled, ButtonOutlined, Flex, Text } from 'Components/Base';
import ExamQuestion from 'Routes/GuidedPractice/Components/ExamRoom/ExamQuestion';
import InnerLayout from 'Routes/GuidedPractice/Components/InnerLayout';
import { EXAM_PAPER_ITEM_TYPE } from 'Components/Quiz/constant';
import SolutionDetailsModal from 'Routes/GuidedPractice/Components/ExamRoom/SolutionDetailsModal';
import EmptyState from 'Routes/GuidedPractice/Components/EmptyState';
import { EXAM_PAPER_TYPE } from 'Context/ExamRoomContext/mapper';
import ExamArticle from 'Routes/GuidedPractice/Components/ExamRoom/ExamArticle';

const Wrapper = styled.div`
  overflow: auto;

  /* Webkit browsers (Chrome, Safari) */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Firefox */
  scrollbar-width: none;
`;

const StyledButtonOutline = styled((props) => (
  <ButtonOutlined color="white" variant={props.isMobile ? 'small' : 'medium'} {...props} />
))`
  ${({ theme, isMobile }) => css`
    border-radius: 300px;
    background-color: ${theme.colors.white};
    color: ${theme.colors.text};
    padding: ${({ theme }) =>
      `${isMobile ? theme.space[1] : theme.space[2]}px ${
        isMobile ? theme.space[2] : theme.space[3]
      }px`};
    height: ${isMobile ? '40px' : '48px'};
    &:hover {
      background-color: ${theme.colors.greyscale.grey5};
    }
  `}
`;

const StyledButtonFilled = styled((props) => (
  <ButtonFilled
    color="backgroundNeutral"
    variant={props.isMobile ? 'small' : 'medium'}
    {...props}
  />
))`
  ${({ theme, isMobile }) => css`
    border-radius: 300px;
    height: ${isMobile ? '40px' : '48px'};
    &:not(:disabled):hover {
      background-color: ${theme.colors.greyscale.grey1};
    }
  `}
`;

const ExamResultItem = ({
  examResultSelected,
  setExamResultSelected,
  totalQuestionAmount,
  currentQuestionNumber,
  onNext,
}) => {
  const [isOpenSolutionDetails, setIsOpenSolutionDetails] = useState(false);
  const { t } = useTranslation('guidedPractice');
  const { isMobile } = useCompatibleDisplay();
  const ref = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const selectedIndices = useMemo(() => {
    if (!examResultSelected?.metadataV2?.choices) return [];

    switch (examResultSelected.questionType) {
      case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
        if (!examResultSelected.answer?.value) return [];
        return getAnswerIndices(
          examResultSelected.answer.value,
          examResultSelected.metadataV2.choices
        );
      case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE: {
        const singleAnswerIndex = examResultSelected.metadataV2.choices.findIndex(
          (choice) => choice.value === examResultSelected?.answer?.value
        );
        return singleAnswerIndex !== -1 ? [singleAnswerIndex] : [];
      }
      default:
        return [];
    }
  }, [examResultSelected]);

  const correctAnswerIndices = useMemo(() => {
    if (!examResultSelected?.metadataV2?.choices) return [];

    switch (examResultSelected.questionType) {
      case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
        if (!examResultSelected.correctAnswer?.value) return [];
        return getAnswerIndices(
          examResultSelected.correctAnswer.value,
          examResultSelected.metadataV2.choices
        );
      case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE: {
        const correctIndex = examResultSelected.metadataV2.choices.findIndex(
          (choice) => choice.value === examResultSelected?.correctAnswer?.value
        );
        return correctIndex !== -1 ? [correctIndex] : [];
      }
      default:
        return [];
    }
  }, [examResultSelected]);

  const handleNextExamResult = () => {
    setIsOpenSolutionDetails(false);
    setIsTransitioning(true);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
    onNext();
  };

  const handleConfirmBackToExamResultList = () => {
    setExamResultSelected(null);
  };

  useEffect(() => {
    ref.current?.scrollTo(0, 0);
  }, [examResultSelected]);

  return (
    <InnerLayout style={{ paddingTop: 32 }}>
      <Flex alignItems="center" justifyContent="space-between" mb={6}>
        <StyledButtonOutline
          isMobile={isMobile}
          color="greyscale.grey4"
          onClick={handleConfirmBackToExamResultList}
        >
          {t('BUTTON.BACK')}
        </StyledButtonOutline>
        {examResultSelected.type === EXAM_PAPER_TYPE.QUESTION && (
          <Text>
            คำถามที่ {currentQuestionNumber} จาก {totalQuestionAmount}
          </Text>
        )}
      </Flex>
      {examResultSelected.type === EXAM_PAPER_TYPE.ARTICLE ? (
        <Wrapper ref={ref} style={{ overflow: 'auto' }}>
          <Flex alignItems="center" justifyContent="center" flexDirection="column" gridRowGap={4}>
            <ExamArticle
              article={examResultSelected.details}
              containerClassName={isTransitioning ? 'fade-item fade-out' : 'fade-item fade-in'}
            />
            <StyledButtonFilled isMobile={isMobile} onClick={handleNextExamResult}>
              {t('BUTTON.GO')}
            </StyledButtonFilled>
          </Flex>
        </Wrapper>
      ) : examResultSelected.questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE ||
        examResultSelected.questionType === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT ? (
        <Flex ref={ref} flexDirection={'column'} gridRowGap={6}>
          <ExamQuestion
            variant="solution"
            containerClassName={isTransitioning ? 'fade-item fade-out' : 'fade-item fade-in'}
            question={examResultSelected.question}
            choices={examResultSelected.metadataV2.choices}
            isCorrect={examResultSelected.isCorrect}
            selectedChoices={selectedIndices}
            correctAnswerIndices={correctAnswerIndices}
            footerAction={
              <Flex gridColumnGap={4} justifyContent="flex-end">
                <StyledButtonOutline
                  isMobile={isMobile}
                  onClick={() => {
                    setIsOpenSolutionDetails(true);
                  }}
                >
                  {t('BUTTON.SEE_SOLUTION')}
                </StyledButtonOutline>
                <StyledButtonFilled isMobile={isMobile} onClick={handleNextExamResult}>
                  {t('BUTTON.GO')}
                </StyledButtonFilled>
              </Flex>
            }
            questionType={examResultSelected.questionType}
          />
          <SolutionDetailsModal
            muiProps={{
              isActive: isOpenSolutionDetails,
              onClose: handleNextExamResult,
            }}
            isCorrect={examResultSelected.isCorrect}
            solution={examResultSelected.solution}
          />
        </Flex>
      ) : (
        <EmptyState
          title={`ระบบแบบฝึกทบทวนไม่รองรับข้อสอบประเภท "${examResultSelected.questionType}"`}
          description={`(ตอนนี้ระบบรองรับประเภทตัวเลือกแบบคำตอบเดียวเท่านั้น)`}
          action={
            <StyledButtonFilled
              isMobile={isMobile}
              width={168}
              mt={4}
              onClick={handleConfirmBackToExamResultList}
            >
              {t('BUTTON.BACK')}
            </StyledButtonFilled>
          }
        />
      )}
    </InnerLayout>
  );
};

ExamResultItem.propTypes = {
  examResultSelected: PropTypes.shape({
    questionType: PropTypes.oneOf(Object.values(EXAM_PAPER_ITEM_TYPE)),
    question: PropTypes.string,
    metadataV2: PropTypes.shape({
      choices: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
        })
      ),
    }),
    correctAnswer: PropTypes.shape({
      value: PropTypes.string,
    }),
    answer: PropTypes.shape({
      value: PropTypes.string,
    }),
    isCorrect: PropTypes.bool,
    solution: PropTypes.string,
    type: PropTypes.oneOf(Object.values(EXAM_PAPER_TYPE)).isRequired,
    details: PropTypes.string,
  }),
  setExamResultSelected: PropTypes.func.isRequired,
  totalQuestionAmount: PropTypes.number.isRequired,
  currentQuestionNumber: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default ExamResultItem;
